<template>
  <transition name="toast-slide">
    <div v-if="show" class="ism-toast-wrap" :class="toastClass">
      <!-- Icon -->
      <div class="ism-toast-icon">
        <v-icon :color="iconColor" size="18">{{ iconName }}</v-icon>
      </div>

      <!-- Content -->
      <div class="ism-toast-content">
        <div class="ism-toast-message">{{ message }}</div>
      </div>

      <!-- Close -->
      <button class="ism-toast-close" @click="show = false">
        <v-icon size="14" color="#94a3b8">mdi-close</v-icon>
      </button>

      <!-- Progress bar -->
      <div class="ism-toast-progress" :style="{ animationDuration: timeout + 'ms', background: progressColor }"></div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'Alert',
  data() {
    return { show: false };
  },
  computed: {
    ...mapState('alert', ['showAlert', 'variant', 'timeout', 'message']),

    /* Map old Vuetify color variants → type */
    type() {
      const v = (this.variant || '').toLowerCase();
      if (v.includes('green') || v.includes('success')) return 'success';
      if (v.includes('red') || v.includes('error'))     return 'error';
      if (v.includes('amber') || v.includes('warning')) return 'warning';
      return 'info';
    },

    toastClass() {
      return `ism-toast--${this.type}`;
    },

    iconName() {
      const map = {
        success: 'mdi-check-circle',
        error:   'mdi-alert-circle',
        warning: 'mdi-alert',
        info:    'mdi-information',
      };
      return map[this.type] || 'mdi-information';
    },

    iconColor() {
      const map = {
        success: '#10b981',
        error:   '#ef4444',
        warning: '#f59e0b',
        info:    '#3b82f6',
      };
      return map[this.type] || '#3b82f6';
    },

    progressColor() {
      return this.iconColor;
    },
  },

  watch: {
    showAlert(val) {
      if (val) {
        this.show = true;
        setTimeout(() => { this.show = false; }, Number(this.timeout) || 3000);
      }
    },
  },
};
</script>

<style scoped>
/* Wrapper */
.ism-toast-wrap {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  min-width: 300px;
  max-width: 400px;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, rgba(0,0,0,0.07));
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  overflow: hidden;
  font-family: "Inter", sans-serif;
}

/* Left accent border */
.ism-toast--success { border-left: 4px solid #10b981; }
.ism-toast--error   { border-left: 4px solid #ef4444; }
.ism-toast--warning { border-left: 4px solid #f59e0b; }
.ism-toast--info    { border-left: 4px solid #3b82f6; }

/* Icon */
.ism-toast-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ism-toast--success .ism-toast-icon { background: rgba(16,185,129,0.10); }
.ism-toast--error   .ism-toast-icon { background: rgba(239,68,68,0.10); }
.ism-toast--warning .ism-toast-icon { background: rgba(245,158,11,0.10); }
.ism-toast--info    .ism-toast-icon { background: rgba(59,130,246,0.10); }

/* Content */
.ism-toast-content { flex: 1; }
.ism-toast-message {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary, #1e293b);
  line-height: 1.4;
}

/* Close button */
.ism-toast-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  transition: opacity 0.15s;
}
.ism-toast-close:hover { opacity: 1; }

/* Progress bar */
.ism-toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  border-radius: 0 0 14px 14px;
  animation: toastProgress linear forwards;
  opacity: 0.7;
}

@keyframes toastProgress {
  from { width: 100%; }
  to   { width: 0%; }
}

/* Slide transition */
.toast-slide-enter-active { animation: toastIn 0.32s cubic-bezier(0.34,1.56,0.64,1); }
.toast-slide-leave-active  { animation: toastOut 0.22s ease forwards; }

@keyframes toastIn {
  from { opacity: 0; transform: translateX(40px) scale(0.95); }
  to   { opacity: 1; transform: none; }
}
@keyframes toastOut {
  from { opacity: 1; transform: none; }
  to   { opacity: 0; transform: translateX(40px) scale(0.95); }
}
</style>